'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import { type ChipValue } from '../Input/FilterChip'
import {
  QueryExpression,
  type QueryExpressionType,
} from './components/QueryExpression'
import { QueryCondition } from './components/QueryChip'
// Define ResourceType loosely for now since QueryResources component doesn't exist
import { QueryBuilder } from './components/QueryBuilder'
import type { QueryBuilderHandle } from './components/QueryBuilder'
import Icon from '../Icon'
import {
  formatRelativeDateFilterLabel,
  isRelativeDateFilterValue,
} from './relativeDateFilter'
import { QuerySearch } from './components/QuerySearch'
import { QueryLayout } from './components/QueryLayout'
import type { LayoutOptionsPanelProps } from '../LayoutOptionsPanel'
import { Button, buttonVariants } from '../Button'
import type {
  CalendarSyncDirection,
  CalendarSyncStatus,
} from '../DataView/Calendar/types'

export type QueryField = {
  label: string
  value: string
  modelLabel: string
  modelvalue: string
  showModelLabel: boolean
  icon?: string
  dataType:
    | 'STRING'
    | 'NUMBER'
    | 'DATE'
    | 'DATE_TIME'
    | 'TIME'
    | 'BOOLEAN'
    | 'RELATION'
  options?: {
    multipleChoice: boolean
    items?: Array<{
      id?: string
      value: string
      label: string
    }>
  }
  /** Visualization helper for grouping field chips in layout menus. */
  section?: { handle: string; name: string } | null
  /** Global FieldDefinition.handle when the field is typed (phone, calendar/recurrence, …). */
  definitionHandle?: string | null
  // Optional async search to fetch selectable options dynamically
  // When provided, the Query chip will render a searchable list and
  // populate options from the callback results.
  searchOptions?: (
    query: string,
  ) => Promise<Array<{ value: string; label: string }>>
}

export type QuerySort =
  | { key: string; direction?: 'asc' | 'desc' }
  | Array<{ key: string; direction?: 'asc' | 'desc' }>

type Comparison =
  | 'eq'
  | 'neq'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'nin'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'is'
  | 'isNot'
  | 'isNotNull'
  | 'isNull'
  | 'isTrue'
  | 'isFalse'
  | 'isNotTrue'
  | 'isNotFalse'
  | 'isNotNotNull'
  | 'day_of_week'
  | 'day_of_month'
  | 'month_of_year'
  | 'hour_of_day'

// New structured filter types that mimic Prisma syntax
export type StructuredFilter = {
  [key: string]: {
    [key: string]:
      | string
      | number
      | boolean
      | Date
      | StructuredFilter
      | StructuredFilter[]
  }
}

export type StructuredExpression =
  | {
      AND?: StructuredFilter[]
      OR?: StructuredFilter[]
      NOT?: StructuredFilter
    }
  | StructuredFilter

// Types for the visual components
type QueryConditionValue = {
  label: string
  value: string
}

type QueryChipData = {
  type: 'chip'
  id: string
  data: {
    field: QueryField
    condition?: QueryConditionValue
    comparison: {
      label: string | null
      value: string | null
      options: ChipValue[]
    }
  }
}

type QuerySearchData = {
  type: 'search'
  id: string
  data: {
    value: string
  }
}

type QueryExpressionData = {
  type: 'expression'
  id: string
  data: {
    expressionType: QueryExpressionType
  }
}

export type QueryItem = QueryChipData | QueryExpressionData | QuerySearchData

// Controlled QueryBar types and props
export type ViewType = 'spreadsheet' | 'kanban' | 'calendar'

export type SectionRelationValue = {
  id: string
  label: string
}

export type SectionRelationConfig = {
  /**
   * QueryField.value for the RELATION field currently used as "section by".
   */
  fieldValue: string
  /**
   * Currently included relation values for this section field.
   */
  values: SectionRelationValue[]
  /**
   * Callback invoked when the user updates the included relation values.
   */
  onChange: (next: SectionRelationValue[]) => void
  /**
   * Remote search used to discover relation values (e.g., Rooms) by label.
   */
  search: (query: string) => Promise<SectionRelationValue[]>
}

export type QueryBarAction =
  | 'filter'
  | 'sort'
  | 'focus'
  | 'active'
  | 'query'
  | 'items'
  | 'columns'
  | 'view'
  | 'section'
  | 'subsection'
  | 'layout'

export type QueryBarState = {
  // source of truth for server/query
  filters: StructuredExpression
  // visual representation for chips/search/expressions
  items: QueryItem[]
  // UI text input
  q: string
  // bar open/closed
  isActive: boolean
  // sort state
  sort?: QuerySort
  // focused chip id
  focusedItemId?: string
  // element id to focus after an action (managed by QueryBar)
  focusElementId?: string
  // view and labels
  view?: ViewType
  selectedSectionLabel?: string
  selectedSubsectionLabel?: string
  // layout/columns selection
  selectedFields: QueryField[]
  layoutSelectedFieldValues?: string[]
  // calendar configuration
  calendarStartField?: QueryField | null
  calendarEndField?: QueryField | null
  calendarRecurrenceField?: QueryField | null
  calendarSeriesIdField?: QueryField | null
  calendarOriginalStartField?: QueryField | null
  calendarField?: QueryField | null
  calendarFieldMap?: Record<string, string>
  visibleCalendarIds?: string[]
  defaultCalendarId?: string
  // calendar bounds (URL-level, not embedded in filters)
  calendarStartAt?: string | null
  // calendar time window (time-of-day in HH:mm format)
  calendarStartTime?: string | null
  calendarEndTime?: string | null
  calendarStepMinutes?: number
  // calendar mode (day | week | month)
  calendarMode?: 'day' | 'week' | 'month'
  // selected weekdays for calendar week mode
  calendarWeekdays?: Array<
    'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'
  >
  // Calendar sync UI preferences (wired by integration later)
  calendarSyncEnabled?: boolean
  calendarSyncDirection?: CalendarSyncDirection
  calendarSyncExternalReadOnly?: boolean
  calendarSyncCalendarIds?: string[]
  calendarSyncStatus?: CalendarSyncStatus
  calendarSyncLastSyncedAt?: string | null
  calendarSyncError?: string | null
  // section/subsection selections (for kanban)
  section?: { field: QueryField | null; groupKey?: string }
  subsection?: { field: QueryField | null; groupKey?: string }
  // kanban-specific field selectors
  kanbanStatusBadgeField?: QueryField | null
  kanbanDescriptionField?: QueryField | null
  // ephemeral delete mode (UI-only)
  deleteModeId?: string | null
}

export type QueryBarProps = {
  // identifier for the block this query bar is for
  blockId: string
  // controlled state + dispatcher
  state: QueryBarState
  onStateChange: (next: QueryBarState, action: QueryBarAction) => void
  // structural inputs
  showableFields: Array<{
    type: 'chip'
    field: QueryField
    isSelected: boolean
    comparison: {
      label: string | null
      value: string | null
      options: ChipValue[]
    }
    showModelLabel?: boolean
  }>
  queryFields: Array<{
    type: 'chip'
    field: QueryField
    comparison: {
      label: string | null
      value: string | null
      options: ChipValue[]
    }
  }>
  // Optional: known relationship prefixes (e.g., "pathology_report", "pathology_report.animal")
  relationshipPrefixes?: string[]
  // Optional: callback to lazily fetch fields for a given dot-prefix
  // Example: "pathology_report.animal." → returns chips for that target model's leaf fields
  fetchFieldsForPrefix?: (prefix: string) => Promise<
    Array<{
      type: 'chip'
      field: QueryField
      comparison: {
        label: string | null
        value: string | null
        options: ChipValue[]
      }
    }>
  >
  className?: string
  sectionableFields?: QueryField[]
  pagination?: {
    page: number
    total: number
    hasMore: boolean
    limit: number
  }
  modelName?: string
  /**
   * Optional configuration for RELATION-based "section by" fields. When
   * provided, the layout/view selector can render a searchable multi-select
   * submenu that lets users pick which related records (e.g., Rooms) should
   * have visible sections.
   */
  sectionRelationConfig?: SectionRelationConfig
  /** When false, hides the layout/view selector (e.g. when rendered inside PageDrawer). */
  showLayoutControls?: boolean
  /** Keep filter field suggestions visible even when the input is empty or unfocused. */
  alwaysShowSuggestions?: boolean
  /** Focus the filter input when mounted or toggled on. */
  autoFocus?: boolean
  /** Resolve persisted layout field selections (Page drawer). */
  resolveLayoutSelectedFields?: () => QueryField[]
  /** Raw persisted layout field value keys (Page drawer). */
  getLayoutSelectedFieldValues?: () => string[]
}

export const QueryBar: React.FC<QueryBarProps> = ({
  blockId,
  state,
  onStateChange,
  showableFields,
  queryFields,
  relationshipPrefixes,
  fetchFieldsForPrefix,
  className,
  sectionableFields,
  pagination,
  modelName,
  sectionRelationConfig,
  showLayoutControls = true,
  resolveLayoutSelectedFields,
  alwaysShowSuggestions: alwaysShowSuggestionsProp,
  autoFocus = false,
  getLayoutSelectedFieldValues,
}: QueryBarProps) => {
  const alwaysShowSuggestions = alwaysShowSuggestionsProp ?? !showLayoutControls
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null)
  const measureRef = React.useRef<HTMLSpanElement>(null)
  const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const builderRef = React.useRef<QueryBuilderHandle>(null)
  const lastProgrammaticFocusAtRef = React.useRef<number>(0)
  // Local active state for snappy UI reaction
  const [isActive, setIsActive] = React.useState<boolean>(state.isActive)
  // Local input state for query string to keep typing responsive
  const [localQuery, setLocalQuery] = React.useState<string>(state.q || '')
  const textAreaValue = localQuery
  const keepExpandedHeight = !showLayoutControls
  const isExpanded = isActive || keepExpandedHeight
  const [localDeleteModeId, setLocalDeleteModeId] = React.useState<
    string | null | undefined
  >(state.deleteModeId ?? null)
  React.useEffect(() => {
    setLocalDeleteModeId(state.deleteModeId ?? null)
  }, [state.deleteModeId])
  const deleteMode = localDeleteModeId ?? null

  // Track chips that are being edited (no value yet) - these can't be stored in URL
  // because convertQueryItemsToStructured skips chips without values
  const [pendingChips, setPendingChips] = React.useState<QueryItem[]>([])

  // Clear pending chips when URL filters change significantly (e.g., after navigation)
  // This ensures chips are derived from URL after navigation
  const prevFiltersRef = React.useRef<string>(JSON.stringify(state.filters))
  React.useEffect(() => {
    const currentFilters = JSON.stringify(state.filters)
    if (prevFiltersRef.current !== currentFilters) {
      // Filters changed - clear pending chips to show URL-derived state
      setPendingChips([])
      prevFiltersRef.current = currentFilters
    }
  }, [state.filters])

  // Merge URL-derived items with pending local chips for display
  // URL is the source of truth for complete chips, local state for incomplete ones
  const internalItems = React.useMemo(() => {
    // Helper to get field value from a chip item
    const getChipFieldValue = (item: QueryItem): string | null => {
      if (item.type !== 'chip') return null
      return item.data.field.value
    }

    // Build a set of field values that exist in URL with values
    const urlFieldsWithValues = new Set<string>()
    for (const item of state.items) {
      if (item.type === 'chip' && item.data.comparison.value) {
        const fieldValue = getChipFieldValue(item)
        if (fieldValue) urlFieldsWithValues.add(fieldValue)
      }
    }

    // Pending chips that should still be shown (not yet in URL with a value for same field)
    const stillPending = pendingChips.filter((pc) => {
      const fieldValue = getChipFieldValue(pc)
      if (!fieldValue) return true
      // If URL has a chip for this field with a value, don't show pending
      return !urlFieldsWithValues.has(fieldValue)
    })

    // Get pending field values to exclude from URL items (avoid duplicates)
    const pendingFieldValues = new Set(
      stillPending
        .map(getChipFieldValue)
        .filter((v): v is string => v !== null),
    )

    // URL items, excluding any fields that are still pending
    const urlItems = state.items.filter((item) => {
      const fieldValue = getChipFieldValue(item)
      if (!fieldValue) return true // non-chip items always included
      return !pendingFieldValues.has(fieldValue)
    })

    return [...urlItems, ...stillPending]
  }, [state.items, pendingChips])

  // Auto-cleanup pending chips that have been synced to URL with values
  React.useEffect(() => {
    // Helper to get field value from a chip item
    const getChipFieldValue = (item: QueryItem): string | null => {
      if (item.type !== 'chip') return null
      return item.data.field.value
    }

    // Build a set of field values that exist in URL with values
    const urlFieldsWithValues = new Set<string>()
    for (const item of state.items) {
      if (item.type === 'chip' && item.data.comparison.value) {
        const fieldValue = getChipFieldValue(item)
        if (fieldValue) urlFieldsWithValues.add(fieldValue)
      }
    }

    // Find pending chips whose field now has a value in URL
    const syncedIds = pendingChips
      .filter((pc) => {
        const fieldValue = getChipFieldValue(pc)
        return fieldValue && urlFieldsWithValues.has(fieldValue)
      })
      .map((pc) => pc.id)

    if (syncedIds.length > 0) {
      setPendingChips((prev) => prev.filter((p) => !syncedIds.includes(p.id)))
    }
  }, [state.items, pendingChips])

  // Keep local active in sync if parent changes it externally
  React.useEffect(() => {
    setIsActive(state.isActive)
  }, [state.isActive])

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
    }
  }, [])

  // Update textarea width based on content
  const updateTextAreaWidth = React.useCallback(() => {
    if (measureRef.current && textAreaRef.current) {
      const width = Math.max(measureRef.current.offsetWidth + 4, 50)
      textAreaRef.current.style.width = `${width}px`
    }
  }, [])

  React.useEffect(() => {
    if (!autoFocus) return
    requestAnimationFrame(() => {
      const el = textAreaRef.current
      if (!el) return
      el.focus()
      const pos = el.value.length
      try {
        el.setSelectionRange(pos, pos)
      } catch {
        // ignore if not supported
      }
    })
  }, [autoFocus])

  // Handle field selection changes
  const handleFieldSelectionChange = React.useCallback(
    (
      field: QueryField,
      isSelected: boolean,
      allSelectedValues: string[] = [],
    ) => {
      const resolvedValues =
        allSelectedValues.length > 0
          ? allSelectedValues
          : (() => {
              const merged = new Set<string>([
                ...(getLayoutSelectedFieldValues?.() ?? []),
                ...state.selectedFields.map((entry) => entry.value),
                ...showableFields
                  .filter((availableField) => availableField.isSelected)
                  .map((availableField) => availableField.field.value),
              ])
              if (isSelected) {
                merged.add(field.value)
              } else {
                merged.delete(field.value)
              }
              return Array.from(merged)
            })()

      const newSelectedFields = resolveSelectedFieldsFromValues(
        resolvedValues,
        showableFields,
        isSelected ? [...state.selectedFields, field] : state.selectedFields,
      )

      onStateChange(
        {
          ...state,
          selectedFields: newSelectedFields,
          layoutSelectedFieldValues: resolvedValues,
          kanbanStatusBadgeField: state.kanbanStatusBadgeField ?? null,
          kanbanDescriptionField: state.kanbanDescriptionField ?? null,
        },
        'columns',
      )
    },
    [getLayoutSelectedFieldValues, onStateChange, showableFields, state],
  )

  React.useEffect(() => {
    updateTextAreaWidth()
  }, [textAreaValue, updateTextAreaWidth])

  // Focus management for specific element ids requested by state
  React.useEffect(() => {
    if (!state.focusElementId) return
    // Mark as programmatic to suppress immediate blur closing
    lastProgrammaticFocusAtRef.current = Date.now()
    const id = state.focusElementId
    const focusNow = () => {
      const el = document.getElementById(id) as HTMLElement | null
      if (el) {
        try {
          ;(el as HTMLInputElement).focus?.()
          // Try to select existing text if it is an input-like element
          ;(el as HTMLInputElement).select?.()
        } catch {
          // ignore
        }
      }
    }
    // Focus the target first (allowing popover content to capture focus), then blur textarea
    requestAnimationFrame(() => {
      focusNow()
      setTimeout(() => {
        const ta = textAreaRef.current
        if (ta) {
          try {
            ta.blur()
          } catch {
            // ignore
          }
        }
        // Clear the focusElementId once done
        onStateChange({ ...state, focusElementId: undefined }, 'active')
      }, 10)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.focusElementId])

  const handleDelete = (id: string) => {
    // Remove from pending chips if present
    setPendingChips((prev) => prev.filter((p) => p.id !== id))

    const newItems = internalItems.filter((item) => item.id !== id)
    if (localDeleteModeId === id) {
      setLocalDeleteModeId(null)
    }
    onStateChange(
      {
        ...state,
        deleteModeId: null,
        items: newItems,
      },
      'items',
    )
  }

  const handleDeleteModeChange = (id: string, isDeleteMode: boolean) => {
    setLocalDeleteModeId(isDeleteMode ? id : null)
    onStateChange(
      {
        ...state,
        deleteModeId: isDeleteMode ? id : null,
      },
      'active',
    )
  }

  const handleParentClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      textAreaRef.current?.focus()
    }
  }

  const handleFocus = () => {
    // Clear any pending blur timeout when focusing
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }
    // Defer activation to avoid interrupting the initial click on inner controls (prevents double-click behavior)
    if (!isActive) {
      setIsActive(true)
    }
  }

  const handleBlur = (e: React.FocusEvent) => {
    // Clear any existing blur timeout
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
    }

    // Only blur if we're not focusing another element within the QueryBar
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      // If we just programmatically opened a chip (within last 200ms), don't close
      const justOpenedChip =
        Date.now() - lastProgrammaticFocusAtRef.current < 800
      // If a chip should remain focused (from the URL or just opened), do not close
      if (state.focusedItemId || justOpenedChip) {
        return
      }
      // Add a small delay to allow for clicks on QueryBuilder items
      blurTimeoutRef.current = setTimeout(() => {
        setIsActive(false)
      }, 150)
    }
  }

  const handleTextAreaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    // Handle Tab: select first non-search suggestion
    if (e.key === 'Tab') {
      const selected = builderRef.current?.selectFirstNonSearch?.() ?? false
      if (selected) {
        e.preventDefault()
        // Keep focus on textarea so typing continues
        requestAnimationFrame(() => {
          const el = textAreaRef.current
          if (el) {
            el.focus()
            const pos = el.value.length
            try {
              el.setSelectionRange(pos, pos)
            } catch {
              // ignore if not supported
            }
          }
        })
        return
      }
    }
    // Handle Backspace: when empty, toggle delete mode on rightmost chip; second backspace deletes it and cleans expressions
    if (e.key === 'Backspace') {
      if (textAreaValue.trim().length === 0) {
        const items = internalItems
        // Find the rightmost deletable item (chip or search)
        let lastDeletableIndex = -1
        for (let i = items.length - 1; i >= 0; i -= 1) {
          const t = (items[i] as { type?: string }).type
          if (t === 'chip' || t === 'search') {
            lastDeletableIndex = i
            break
          }
        }
        if (lastDeletableIndex >= 0) {
          const lastItemId =
            (items[lastDeletableIndex] as { id?: string }).id || undefined
          if (!deleteMode || deleteMode !== lastItemId) {
            // First backspace press: set delete mode on the last item
            setLocalDeleteModeId(lastItemId ?? null)
            onStateChange(
              {
                ...state,
                deleteModeId: lastItemId ?? null,
              },
              'active',
            )
            // Keep focus on textarea
            requestAnimationFrame(() => {
              const el = textAreaRef.current
              if (el) {
                el.focus()
              }
            })
            e.preventDefault()
            return
          }
          // Second backspace while in delete mode: delete item and cleanup expressions
          const nextItems = (() => {
            // Remove the item
            const removed: QueryItem[] = items.filter(
              (_it: QueryItem, idx: number) => idx !== lastDeletableIndex,
            )
            // Cleanup helper: remove leading/trailing expressions and collapse consecutive expressions
            const isExpression = (it: QueryItem) =>
              (it as { type?: string }).type === 'expression'
            // Trim leading expressions
            while (removed.length > 0) {
              const first = removed[0]
              if (!first || !isExpression(first)) break
              removed.shift()
            }
            // Trim trailing expressions
            while (removed.length > 0) {
              const last = removed[removed.length - 1]
              if (!last || !isExpression(last)) break
              removed.pop()
            }
            // Collapse consecutive expressions
            const collapsed: QueryItem[] = []
            for (const it of removed) {
              if (
                collapsed.length > 0 &&
                isExpression(it) &&
                (() => {
                  const prev = collapsed[collapsed.length - 1]
                  return !!prev && isExpression(prev)
                })()
              ) {
                // skip duplicate consecutive expression
                continue
              }
              collapsed.push(it)
            }
            return collapsed
          })()
          // Also remove from pending chips if present
          if (lastItemId) {
            setPendingChips((prev) => prev.filter((p) => p.id !== lastItemId))
          }
          setLocalDeleteModeId(null)
          onStateChange(
            {
              ...state,
              items: nextItems,
              deleteModeId: null,
            },
            'items',
          )
          // Clear local input since we removed an item via backspace flow
          setLocalQuery('')
          // Refocus textarea
          requestAnimationFrame(() => {
            const el = textAreaRef.current
            if (el) {
              el.focus()
            }
          })
          e.preventDefault()
          return
        }
      }
    }

    // Handle Enter key to add a simple text filter
    if (e.key === 'Enter' && textAreaValue.trim()) {
      e.preventDefault()

      // Create a search item
      const searchItem: QuerySearchData = {
        id: `search-${Date.now()}`,
        type: 'search',
        data: {
          value: textAreaValue.trim(),
        },
      }

      const newItems = [...internalItems, searchItem]
      onStateChange({ ...state, items: newItems, q: '' }, 'items')
      // Clear local input immediately for responsiveness
      setLocalQuery('')
    }
  }

  return (
    <div
      className={cn(
        'transition-[min-height] duration-300 ease-out',
        alwaysShowSuggestions
          ? undefined
          : isExpanded
            ? 'min-h-[calc(2.5rem+1rem)]'
            : 'min-h-0',
      )}
    >
      <div
        onClick={handleParentClick}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={-1}
        className={cn(
          'flex flex-wrap items-center  w-full bg-accent/50 p-2 rounded-md hover:bg-accent/50 transition-colors transition-padding duration-300 ease-out focus-within:bg-transparent border border-transparent focus-within:border-foreground cursor-text relative transform-gpu transition-transform origin-top will-change-transform',
          isExpanded
            ? 'min-h-10 scale-y-100 opacity-100'
            : 'min-h-8 py-0 scale-y-95 opacity-95',
          internalItems.length > 0 ? 'gap-2' : 'gap-y-2',
          className,
        )}
      >
        <div className="flex items-center gap-2 px-2">
          <div className="text-xs text-foreground/50">
            <Icon name="ListFilter" size={12} />
          </div>
        </div>

        {/* Display QueryItems using existing components */}
        {internalItems.map((item: QueryItem, index: number) => {
          if (item.type === 'search') {
            return (
              <QuerySearch
                key={item.id}
                value={item.data.value}
                onDelete={() => handleDelete(item.id)}
                isDeleteMode={deleteMode === item.id}
                onDeleteModeChange={(isDeleteMode) =>
                  handleDeleteModeChange(item.id, isDeleteMode)
                }
              />
            )
          }
          if (item.type === 'chip') {
            const chipData = item.data
            return (
              <QueryCondition
                key={item.id}
                field={chipData.field}
                selectedCondition={chipData.condition}
                comparison={chipData.comparison}
                autoOpen={state.focusedItemId === item.id}
                onOpenStateChange={(open) => {
                  if (open) {
                    // Mark programmatic open to prevent container blur from closing immediately
                    lastProgrammaticFocusAtRef.current = Date.now()
                    if (state.focusedItemId !== item.id) {
                      onStateChange(
                        { ...state, focusedItemId: item.id },
                        'focus',
                      )
                    }
                  } else if (state.focusedItemId === item.id) {
                    // When a chip requests to close (e.g., Enter on an input),
                    // always clear focusedItemId to avoid auto-open loops.
                    onStateChange(
                      { ...state, focusedItemId: undefined },
                      'focus',
                    )
                  }
                }}
                onConditionChange={(condition) => {
                  const currentValue = chipData.comparison.value
                  const nextComparisonLabel =
                    currentValue && isRelativeDateFilterValue(currentValue)
                      ? (formatRelativeDateFilterLabel(
                          currentValue,
                          condition.value || '',
                        ) ??
                        chipData.comparison.label)
                      : chipData.comparison.label
                  const newItems = internalItems.map((i: QueryItem) =>
                    i.id === item.id
                      ? ({
                          ...i,
                          data: {
                            ...chipData,
                            condition: {
                              label: condition.label || '',
                              value: condition.value || '',
                            },
                            comparison: {
                              ...chipData.comparison,
                              label: nextComparisonLabel || '',
                            },
                          },
                        } as QueryItem)
                      : i,
                  )
                  onStateChange({ ...state, items: newItems }, 'items')
                }}
                onComparisonChange={(comparison) => {
                  const hasValue = comparison.value && comparison.value !== ''

                  if (!hasValue) {
                    // Value was cleared - move chip to pending so it stays visible
                    // but doesn't sync to URL (which would remove it since empty values are skipped)
                    const updatedChip = {
                      ...item,
                      data: {
                        ...chipData,
                        comparison: {
                          label: comparison.label || '',
                          value: comparison.value || '',
                          options: chipData.comparison.options,
                        },
                      },
                    } as QueryItem

                    // Add to pending if not already there, or update if exists
                    setPendingChips((prev) => {
                      const exists = prev.some((p) => p.id === item.id)
                      if (exists) {
                        // Update existing pending chip
                        return prev.map((p) =>
                          p.id === item.id ? updatedChip : p,
                        )
                      }
                      return [...prev, updatedChip]
                    })

                    // Don't sync to URL - chip would disappear
                    return
                  }

                  // Has value - sync to URL as normal
                  const newItems = internalItems.map((i: QueryItem) =>
                    i.id === item.id
                      ? ({
                          ...i,
                          data: {
                            ...chipData,
                            comparison: {
                              label: comparison.label || '',
                              value: comparison.value || '',
                              options: chipData.comparison.options,
                            },
                          },
                        } as QueryItem)
                      : i,
                  )
                  onStateChange({ ...state, items: newItems }, 'items')
                }}
                onDelete={() => handleDelete(item.id)}
                isDeleteMode={deleteMode === item.id}
                onDeleteModeChange={(isDeleteMode) =>
                  handleDeleteModeChange(item.id, isDeleteMode)
                }
              />
            )
          } else {
            const expressionData = item.data
            return (
              <QueryExpression
                key={item.id}
                type={expressionData.expressionType}
                onDelete={() => handleDelete(item.id)}
                isDeleteMode={deleteMode === item.id}
                onDeleteModeChange={(isDeleteMode) =>
                  handleDeleteModeChange(item.id, isDeleteMode)
                }
              />
            )
          }
        })}

        <div className="relative inline-flex items-center min-w-[50px] flex-grow text-sm">
          <span
            ref={measureRef}
            className="invisible absolute whitespace-pre px-2 text-xs font-[inherit]"
            style={{
              width: 'auto',
              height: 0,
              position: 'absolute',
              whiteSpace: 'pre',
              overflow: 'hidden',
            }}
            aria-hidden="true"
          >
            {textAreaValue || 'Filter or search...'}
          </span>
          <textarea
            ref={textAreaRef}
            value={textAreaValue}
            onChange={(e) => {
              setLocalQuery(e.target.value)
            }}
            onKeyDown={handleTextAreaKeyDown}
            className="focus:outline-none bg-transparent px-1 text-xs resize-none overflow-hidden w-full self-center my-auto"
            rows={1}
            placeholder={
              showLayoutControls
                ? 'Filter or search... (Enter to add, Tab to accept)'
                : 'Filter or search...'
            }
          />
        </div>

        {showLayoutControls && (
          <div className="">
            <QueryLayout
              isFocused={isActive}
              onOpenChange={(open) => {
                if (open) onStateChange({ ...state, isActive: true }, 'active')
              }}
              onSortChange={(newSort) => {
                onStateChange({ ...state, sort: newSort }, 'sort')
              }}
              onViewChange={(v) => onStateChange({ ...state, view: v }, 'view')}
              view={state.view}
              sectionableFields={sectionableFields}
              onSectionChange={(field, groupKey) =>
                onStateChange(
                  { ...state, section: { field, groupKey } },
                  'section',
                )
              }
              onSubsectionChange={(field, groupKey) =>
                onStateChange(
                  { ...state, subsection: { field, groupKey } },
                  'subsection',
                )
              }
              selectedSectionLabel={state.selectedSectionLabel}
              selectedSubsectionLabel={state.selectedSubsectionLabel}
              fields={showableFields.map((field) => ({
                ...field.field,
                isSelected: field.isSelected,
              }))}
              sort={state.sort}
              onFieldSelectionChange={handleFieldSelectionChange}
              pagination={pagination}
              modelName={modelName}
              calendarStartField={state.calendarStartField ?? null}
              calendarEndField={state.calendarEndField ?? null}
              calendarRecurrenceField={state.calendarRecurrenceField ?? null}
              calendarSeriesIdField={state.calendarSeriesIdField ?? null}
              calendarOriginalStartField={
                state.calendarOriginalStartField ?? null
              }
              calendarField={state.calendarField ?? null}
              calendarFieldMap={state.calendarFieldMap}
              queryFields={queryFields.map((entry) => entry.field)}
              onCalendarStartFieldChange={(field) =>
                onStateChange({ ...state, calendarStartField: field }, 'layout')
              }
              onCalendarEndFieldChange={(field) =>
                onStateChange({ ...state, calendarEndField: field }, 'layout')
              }
              onCalendarRecurrenceFieldChange={(field) =>
                onStateChange(
                  { ...state, calendarRecurrenceField: field },
                  'layout',
                )
              }
              onCalendarSeriesIdFieldChange={(field) =>
                onStateChange(
                  { ...state, calendarSeriesIdField: field },
                  'layout',
                )
              }
              onCalendarOriginalStartFieldChange={(field) =>
                onStateChange(
                  { ...state, calendarOriginalStartField: field },
                  'layout',
                )
              }
              onCalendarFieldChange={(field) =>
                onStateChange(
                  {
                    ...state,
                    calendarField: field,
                    ...(field
                      ? { visibleCalendarIds: undefined, defaultCalendarId: undefined }
                      : {
                          calendarFieldMap: {},
                          visibleCalendarIds: undefined,
                          defaultCalendarId: undefined,
                        }),
                  },
                  'layout',
                )
              }
              onCalendarFieldMapChange={(next) =>
                onStateChange({ ...state, calendarFieldMap: next }, 'layout')
              }
              kanbanStatusBadgeField={state.kanbanStatusBadgeField ?? null}
              kanbanDescriptionField={state.kanbanDescriptionField ?? null}
              onKanbanStatusBadgeFieldChange={(field) =>
                onStateChange(
                  { ...state, kanbanStatusBadgeField: field },
                  'layout',
                )
              }
              onKanbanDescriptionFieldChange={(field) =>
                onStateChange(
                  { ...state, kanbanDescriptionField: field },
                  'layout',
                )
              }
              calendarSyncEnabled={state.calendarSyncEnabled}
              calendarSyncDirection={state.calendarSyncDirection}
              calendarSyncExternalReadOnly={state.calendarSyncExternalReadOnly}
              calendarSyncCalendarIds={state.calendarSyncCalendarIds}
              onCalendarSyncEnabledChange={(enabled) =>
                onStateChange(
                  { ...state, calendarSyncEnabled: enabled },
                  'layout',
                )
              }
              onCalendarSyncDirectionChange={(direction) =>
                onStateChange(
                  { ...state, calendarSyncDirection: direction },
                  'layout',
                )
              }
              onCalendarSyncExternalReadOnlyChange={(externalReadOnly) =>
                onStateChange(
                  { ...state, calendarSyncExternalReadOnly: externalReadOnly },
                  'layout',
                )
              }
              onCalendarSyncCalendarIdsChange={(calendarIds) =>
                onStateChange(
                  { ...state, calendarSyncCalendarIds: calendarIds },
                  'layout',
                )
              }
              sectionRelationConfig={sectionRelationConfig}
            />
          </div>
        )}
      </div>

      {/* QueryBuilder for adding new items */}
      <div
        onClick={(e) => {
          // Prevent blur when clicking on QueryBuilder
          e.stopPropagation()
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
          }
        }}
        onMouseEnter={() => {
          // Keep active when hovering over QueryBuilder
          if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current)
            blurTimeoutRef.current = null
          }
        }}
      >
        <QueryBuilder
          ref={builderRef}
          items={internalItems}
          onChangeAction={(newItems) => {
            // Clear any pending blur timeout when QueryBuilder is used
            if (blurTimeoutRef.current) {
              clearTimeout(blurTimeoutRef.current)
              blurTimeoutRef.current = null
            }

            // Animate the new item when it's added
            const lastItem = newItems[newItems.length - 1]
            if (
              lastItem &&
              !internalItems.find((item: QueryItem) => item.id === lastItem.id)
            ) {
              requestAnimationFrame(() => {
                const element = document.querySelector(
                  `[data-id="${lastItem.id}"]`,
                )
                if (element) {
                  element.classList.add('animate-bounce-in')
                }
              })
            }

            // Check if this is a new chip without a value (incomplete chip)
            const isNewIncompleteChip =
              lastItem &&
              lastItem.type === 'chip' &&
              !lastItem.data?.comparison?.value &&
              !internalItems.find((i) => i.id === lastItem.id)

            // Determine if the newly added chip should auto-open a focus component (e.g., dropdown/search)
            const shouldAutoOpenFocusComponent =
              lastItem &&
              lastItem.type === 'chip' &&
              !!(
                lastItem.data?.field?.options?.items?.length ||
                typeof lastItem.data?.field?.searchOptions === 'function' ||
                // Input-based chips (STRING/NUMBER) should autofocus the input as well
                lastItem.data?.field?.dataType === 'STRING' ||
                lastItem.data?.field?.dataType === 'NUMBER'
              )

            // Set ref BEFORE state update and blur so blur handler can check it
            if (shouldAutoOpenFocusComponent) {
              lastProgrammaticFocusAtRef.current = Date.now()
            }

            if (isNewIncompleteChip) {
              // Add to pending chips locally - don't update URL yet since chip has no value
              setPendingChips((prev) => [...prev, lastItem])

              // Still need to handle focus for the new chip
              if (shouldAutoOpenFocusComponent) {
                // Manually trigger focus state update without syncing items to URL
                onStateChange(
                  {
                    ...state,
                    q: '',
                    focusedItemId: lastItem?.id,
                    focusElementId:
                      lastItem.data?.field?.options?.items?.length ||
                      typeof lastItem.data?.field?.searchOptions === 'function'
                        ? `${String(lastItem.data?.field?.value)}-select-search`
                        : `${String(lastItem.data?.field?.value)}-comparison-input`,
                  },
                  'focus',
                )
              }
            } else {
              // Chip has a value or is not a chip - sync to URL
              // Remove from pending if it was there
              if (lastItem) {
                setPendingChips((prev) =>
                  prev.filter((p) => p.id !== lastItem.id),
                )
              }

              // Update state and optionally set focusedItemId so the chip popover opens
              onStateChange(
                {
                  ...state,
                  items: newItems,
                  q: '',
                  ...(shouldAutoOpenFocusComponent
                    ? {
                        focusedItemId: lastItem?.id,
                        focusElementId:
                          // For select/remote chips focus the search input; for input chips focus the comparison input
                          lastItem.data?.field?.options?.items?.length ||
                          typeof lastItem.data?.field?.searchOptions ===
                            'function'
                            ? `${String(lastItem.data?.field?.value)}-select-search`
                            : `${String(lastItem.data?.field?.value)}-comparison-input`,
                      }
                    : {}),
                },
                'items',
              )
            }

            // Clear local input on any add action from builder
            setLocalQuery('')
            // Conditionally keep focus only when a nested resource chip was added
            if (
              lastItem &&
              lastItem.type === 'chip' &&
              typeof lastItem.data?.field?.value === 'string' &&
              lastItem.data.field.value.includes('.')
            ) {
              // Only keep focus if the chip did NOT auto-open a focus component
              if (!shouldAutoOpenFocusComponent) {
                requestAnimationFrame(() => {
                  const el = textAreaRef.current
                  if (el) {
                    el.focus()
                    const pos = el.value.length
                    try {
                      el.setSelectionRange(pos, pos)
                    } catch {
                      // ignore if not supported
                    }
                  }
                })
              }
            } else if (shouldAutoOpenFocusComponent) {
              // If a chip with focus component was added (e.g., a dropdown), blur the textarea
              // Use a small delay to allow the chip popover to mount first
              setTimeout(() => {
                const el = textAreaRef.current
                if (el) {
                  try {
                    el.blur()
                  } catch {
                    // ignore if not supported
                  }
                }
              }, 50)
            }
          }}
          queryFields={queryFields}
          isActive={isActive}
          onAnimationComplete={() => {}}
          q={textAreaValue}
          onQueryChange={(next) => {
            // Keep typing local for responsiveness; do not emit onStateChange on every keystroke
            setLocalQuery(next)
            // Keep focus on the textarea so typing can continue fluidly
            requestAnimationFrame(() => {
              const el = textAreaRef.current
              if (el) {
                el.focus()
                const pos = el.value.length
                try {
                  el.setSelectionRange(pos, pos)
                } catch {
                  // ignore if not supported
                }
              }
            })
          }}
          relationshipPrefixes={relationshipPrefixes}
          fetchFieldsForPrefix={fetchFieldsForPrefix}
          alwaysShowSuggestions={alwaysShowSuggestions}
        />
      </div>
    </div>
  )
}

function resolveSelectedFieldsFromValues(
  values: Iterable<string>,
  showableFields: Array<{ field: QueryField; isSelected: boolean }>,
  fallbackFields: QueryField[],
): QueryField[] {
  const resolved: QueryField[] = []
  for (const value of values) {
    const match =
      showableFields.find((entry) => entry.field.value === value)?.field ??
      fallbackFields.find((field) => field.value === value) ??
      null
    if (match && !resolved.some((field) => field.value === match.value)) {
      resolved.push(match)
    }
  }
  return resolved
}

export function buildLayoutOptionsPanelProps(
  queryBar: Pick<
    QueryBarProps,
    | 'state'
    | 'onStateChange'
    | 'showableFields'
    | 'queryFields'
    | 'sectionableFields'
    | 'sectionRelationConfig'
    | 'getLayoutSelectedFieldValues'
  >,
): Omit<LayoutOptionsPanelProps, 'view' | 'onViewChange'> {
  const {
    state,
    onStateChange,
    showableFields,
    queryFields,
    sectionableFields,
    sectionRelationConfig,
    getLayoutSelectedFieldValues,
  } = queryBar

  const getSelectedFieldValues = (): string[] => {
    const fromStore = getLayoutSelectedFieldValues?.() ?? []
    if (fromStore.length > 0) {
      return fromStore
    }
    if (state.selectedFields.length > 0) {
      return state.selectedFields.map((field) => field.value)
    }
    return showableFields
      .filter((availableField) => availableField.isSelected)
      .map((availableField) => availableField.field.value)
  }

  const getCurrentSelectedFields = (): QueryField[] => {
    const values = getSelectedFieldValues()
    const fields: QueryField[] = []
    for (const value of values) {
      const match =
        showableFields.find((entry) => entry.field.value === value)?.field ??
        state.selectedFields.find((field) => field.value === value) ??
        null
      if (match && !fields.some((field) => field.value === match.value)) {
        fields.push(match)
      }
    }
    return fields
  }

  const selectedFieldValues = new Set(getSelectedFieldValues())

  return {
    fields: showableFields.map((field) => ({
      ...field.field,
      isSelected: selectedFieldValues.has(field.field.value),
    })),
    sectionableFields,
    sort: state.sort,
    onSortChange: (newSort) =>
      onStateChange({ ...state, sort: newSort }, 'sort'),
    onFieldSelectionChange: (field, isSelected, allSelectedValues) => {
      const newSelectedFields = resolveSelectedFieldsFromValues(
        allSelectedValues,
        showableFields,
        [...state.selectedFields, field],
      )

      onStateChange(
        {
          ...state,
          selectedFields: newSelectedFields,
          layoutSelectedFieldValues: allSelectedValues,
          kanbanStatusBadgeField: state.kanbanStatusBadgeField ?? null,
          kanbanDescriptionField: state.kanbanDescriptionField ?? null,
        },
        'columns',
      )
    },
    onSectionChange: (field, groupKey) =>
      onStateChange({ ...state, section: { field, groupKey } }, 'section'),
    onSubsectionChange: (field, groupKey) =>
      onStateChange(
        { ...state, subsection: { field, groupKey } },
        'subsection',
      ),
    selectedSectionLabel: state.selectedSectionLabel,
    selectedSubsectionLabel: state.selectedSubsectionLabel,
    calendarStartField: state.calendarStartField ?? null,
    calendarEndField: state.calendarEndField ?? null,
    calendarRecurrenceField: state.calendarRecurrenceField ?? null,
    calendarSeriesIdField: state.calendarSeriesIdField ?? null,
    calendarOriginalStartField: state.calendarOriginalStartField ?? null,
    calendarField: state.calendarField ?? null,
    calendarFieldMap: state.calendarFieldMap,
    queryFields: queryFields?.map((entry) => entry.field),
    onCalendarStartFieldChange: (field) =>
      onStateChange({ ...state, calendarStartField: field }, 'layout'),
    onCalendarEndFieldChange: (field) =>
      onStateChange({ ...state, calendarEndField: field }, 'layout'),
    onCalendarRecurrenceFieldChange: (field) =>
      onStateChange({ ...state, calendarRecurrenceField: field }, 'layout'),
    onCalendarSeriesIdFieldChange: (field) =>
      onStateChange({ ...state, calendarSeriesIdField: field }, 'layout'),
    onCalendarOriginalStartFieldChange: (field) =>
      onStateChange({ ...state, calendarOriginalStartField: field }, 'layout'),
    onCalendarFieldChange: (field) =>
      onStateChange(
        {
          ...state,
          calendarField: field,
          ...(field
            ? { visibleCalendarIds: undefined, defaultCalendarId: undefined }
            : {
                calendarFieldMap: {},
                visibleCalendarIds: undefined,
                defaultCalendarId: undefined,
              }),
        },
        'layout',
      ),
    onCalendarFieldMapChange: (next) =>
      onStateChange({ ...state, calendarFieldMap: next }, 'layout'),
    kanbanStatusBadgeField: state.kanbanStatusBadgeField ?? null,
    kanbanDescriptionField: state.kanbanDescriptionField ?? null,
    onKanbanStatusBadgeFieldChange: (field) =>
      onStateChange(
        {
          ...state,
          kanbanStatusBadgeField: field,
          kanbanDescriptionField: state.kanbanDescriptionField ?? null,
        },
        'layout',
      ),
    onKanbanDescriptionFieldChange: (field) =>
      onStateChange(
        {
          ...state,
          kanbanDescriptionField: field,
          kanbanStatusBadgeField: state.kanbanStatusBadgeField ?? null,
        },
        'layout',
      ),
    sectionRelationConfig,
  }
}
